import * as PIXI from 'pixi.js';
import { isWallMountedItem, getTileDims } from '../../../domain/tileMath';
import {
  checkSurfaceOverlap,
  getFurnitureDepthComponents,
  getOccupiedBaseTile,
  computeSurfaceTileMap,
} from '../../../domain/depthMath';
import {
  snapWindowAnchorToGrid as snapWindowAnchorToGridImported,
  WINDOW_VERTICAL_OFFSET,
  TILE_WIDTH,
  TILE_HEIGHT,
  MAP_WIDTH_TILES,
} from '../../../domain/windowSnap';
import { asFurnitureMarker, asFurnitureSprite, liveFurnitureData } from './pixiExtensions';
import { OPAQUE_PIXEL_ALPHA_THRESHOLD } from './alphaMask';

// Minimum number of opaque sample points (out of 9) for a rug tile to count as
// "significantly occupied". Tiles with fewer opaque hits are treated as empty —
// this removes barely-touching edge tiles on round rugs. 4/9 ≈ 44% ensures a
// tile is only highlighted when the rug covers it meaningfully, not just
// clipping a corner (which can still hit 3 clustered sample points).
const RUG_TILE_MIN_OPAQUE_HITS = 4;

export function redrawOccupiedTilesOverlay(
  markers: Map<number, PIXI.Container>,
  occupiedTilesOverlay: PIXI.Graphics | null,
  showGridMode: boolean,
): void {
  const overlay = occupiedTilesOverlay;
  if (!overlay) return;

  // ALWAYS check showGridMode first - use ref to avoid closure issues
  // This prevents overlay from showing during drag operations when grid is disabled
  const isGridEnabled = showGridMode;
  if (!isGridEnabled) {
    overlay.visible = false;
    overlay.clear();
    return;
  }

  // Show overlay if grid mode is on
  overlay.visible = true;
  overlay.clear();
  overlay.eventMode = 'none';
  overlay.alpha = 0.85;

  const halfTileWidth = TILE_WIDTH / 2;
  const halfTileHeight = TILE_HEIGHT / 2;

  const drawTileDiamond = (tileX: number, tileY: number, fillColor: number, fillAlpha: number) => {
    // `tileX/tileY` are logical cell indices.
    // Cell center in isometric grid is shifted by +1 half-tile on Y
    // from node-space transform.
    const centerX = (tileX - tileY) * halfTileWidth;
    const centerY = (tileX + tileY + 1) * halfTileHeight;
    overlay.poly(
      [
        centerX, centerY - halfTileHeight,
        centerX + halfTileWidth, centerY,
        centerX, centerY + halfTileHeight,
        centerX - halfTileWidth, centerY,
        centerX, centerY - halfTileHeight,
      ],
      false
    );
    overlay.fill({ color: fillColor, alpha: fillAlpha });
    overlay.stroke({ width: 1, color: 0x8a4f00, alpha: 0.8 });
  };

  // Build surface map once so we can detect items sitting on surfaces and skip
  // drawing their floor-level footprints (they're shown via purple surface tiles).
  const surfaceMap = computeSurfaceTileMap(markers);

  for (const [fId, marker] of markers.entries()) {
    const fm = asFurnitureMarker(marker)!;
    const sprite = asFurnitureSprite(marker.children[0] as PIXI.Sprite | undefined);
    const furniture = liveFurnitureData(sprite, fm);
    if (!furniture) continue;

    const isWindowItem = isWallMountedItem(furniture.furniture_catalog);

    // For windows/wall-mounted: temporarily subtract WINDOW_VERTICAL_OFFSET so tile calculation
    // uses floor-level coordinates (same approach as validateWindowPlacement).
    const originalY = marker.position.y;
    if (isWindowItem) {
      marker.position.y = originalY - WINDOW_VERTICAL_OFFSET;
    }

    const { w: widthTiles, h: heightTiles, isRug: itemIsRug } = getTileDims(furniture.furniture_catalog);
    const depth = getFurnitureDepthComponents(marker, widthTiles, heightTiles, itemIsRug);

    // Restore original position for windows/wall-mounted
    if (isWindowItem) {
      marker.position.y = originalY;
    }
    
    const isMirrored = !!furniture.is_mirrored;
    const fillColor = isWindowItem
      ? (furniture.is_preview ? 0x66bbff : 0x3399ff)   // blue tint for windows
      : (furniture.is_preview ? 0xffc266 : 0xffa500);

    if (itemIsRug && sprite) {
      // ── Rug overlay: alpha-mask pixel-accurate approach ──
      // Rugs are flat on the floor. We sample the sprite's alpha mask at each
      // candidate tile's key points (center + 4 diamond vertices) to check
      // whether the tile actually intersects with opaque rug pixels.
      const spriteW = Math.abs(sprite.width);
      const spriteH = Math.abs(sprite.height);
      const spriteGX = marker.position.x + sprite.x;
      const spriteGY = marker.position.y + sprite.y;
      const isMirroredSprite = sprite.scale.x < 0;
      const rugAlpha = sprite.__alphaMask;

      // Sprite bounding box in world coords (anchor 0.5, 1.0)
      const boxLeft = spriteGX - spriteW / 2;
      const boxRight = spriteGX + spriteW / 2;
      const boxTop = spriteGY - spriteH;
      const boxBottom = spriteGY;

      // Check if a world-space point maps to an opaque rug pixel.
      const isOpaqueAt = (wx: number, wy: number): boolean => {
        if (wx < boxLeft || wx > boxRight || wy < boxTop || wy > boxBottom) return false;
        if (!rugAlpha) return true; // no mask loaded yet → treat bbox interior as opaque
        const localX = wx - spriteGX;
        const localY = wy - spriteGY;
        let u = (localX + spriteW / 2) / spriteW;
        const v = (localY + spriteH) / spriteH;
        if (isMirroredSprite) u = 1.0 - u;
        if (u < 0 || u >= 1 || v < 0 || v >= 1) return false;
        const px = Math.floor(u * rugAlpha.width);
        const py = Math.floor(v * rugAlpha.height);
        if (px < 0 || px >= rugAlpha.width || py < 0 || py >= rugAlpha.height) return false;
        return rugAlpha.data[py * rugAlpha.width + px] > OPAQUE_PIXEL_ALPHA_THRESHOLD;
      };

      // Convert box corners to tile coords for the scanning range
      const corners = [
        [boxLeft, boxTop], [boxRight, boxTop],
        [boxLeft, boxBottom], [boxRight, boxBottom],
      ];
      let minTX = Infinity, maxTX = -Infinity;
      let minTY = Infinity, maxTY = -Infinity;
      for (const [cx, cy] of corners) {
        const tx = (cx / halfTileWidth + cy / halfTileHeight) / 2;
        const ty = (cy / halfTileHeight - cx / halfTileWidth) / 2;
        minTX = Math.min(minTX, tx);
        maxTX = Math.max(maxTX, tx);
        minTY = Math.min(minTY, ty);
        maxTY = Math.max(maxTY, ty);
      }
      const startTX = Math.max(0, Math.floor(minTX));
      const endTX = Math.min(MAP_WIDTH_TILES - 1, Math.ceil(maxTX));
      const startTY = Math.max(0, Math.floor(minTY));
      const endTY = Math.min(MAP_WIDTH_TILES - 1, Math.ceil(maxTY));

      // For each candidate tile, sample 9 points (center + 4 vertices + 4 edge
      // midpoints) and require at least RUG_TILE_MIN_OPAQUE_HITS opaque hits.
      // This removes tiles that the rug barely touches (< ~22% coverage).
      const qTW = halfTileWidth / 2;
      const qTH = halfTileHeight / 2;
      for (let tx = startTX; tx <= endTX; tx++) {
        for (let ty = startTY; ty <= endTY; ty++) {
          const cx = (tx - ty) * halfTileWidth;
          const cy = (tx + ty + 1) * halfTileHeight;
          let hits = 0;
          // center
          if (isOpaqueAt(cx, cy)) hits++;
          // 4 vertices
          if (isOpaqueAt(cx, cy - halfTileHeight)) hits++;
          if (isOpaqueAt(cx + halfTileWidth, cy)) hits++;
          if (isOpaqueAt(cx, cy + halfTileHeight)) hits++;
          if (isOpaqueAt(cx - halfTileWidth, cy)) hits++;
          // 4 edge midpoints (only if still below threshold to short-circuit)
          if (hits < RUG_TILE_MIN_OPAQUE_HITS) {
            if (isOpaqueAt(cx + qTW, cy - qTH)) hits++;
            if (hits < RUG_TILE_MIN_OPAQUE_HITS && isOpaqueAt(cx + qTW, cy + qTH)) hits++;
            if (hits < RUG_TILE_MIN_OPAQUE_HITS && isOpaqueAt(cx - qTW, cy + qTH)) hits++;
            if (hits < RUG_TILE_MIN_OPAQUE_HITS && isOpaqueAt(cx - qTW, cy - qTH)) hits++;
          }
          if (hits >= RUG_TILE_MIN_OPAQUE_HITS) {
            drawTileDiamond(tx, ty, fillColor, 0.28);
          }
        }
      }
    } else if (isWindowItem) {
      // ── Window overlay: use snap logic for correct wall-flush baseTile ──
      // getOccupiedBaseTile doesn't work for windows because:
      //   1. The anchor position doesn't map to "front node" the same way as floor furniture
      //   2. Mirror-swapped overlay dimensions cause a 1-tile gap from the wall
      // Instead, use the snap function which already handles wall-forcing correctly.
      const floorX = marker.position.x;
      const floorY = originalY - WINDOW_VERTICAL_OFFSET;  // floor-level Y
      const snapResult = snapWindowAnchorToGridImported(
        floorX, floorY,
        depth.effectiveWidthTiles,
        depth.effectiveHeightTiles,
      );
      // Overlay dimensions: mirror-swap so thickness=1 aligns with perpendicular wall axis
      const overlayWidthTiles = isMirrored ? depth.effectiveHeightTiles : depth.effectiveWidthTiles;
      const overlayHeightTiles = isMirrored ? depth.effectiveWidthTiles : depth.effectiveHeightTiles;
      for (let tx = 0; tx < overlayWidthTiles; tx++) {
        for (let ty = 0; ty < overlayHeightTiles; ty++) {
          drawTileDiamond(snapResult.baseTileX + tx, snapResult.baseTileY + ty, fillColor, 0.28);
        }
      }
    } else {
      // ── Non-rug furniture: use front-node based overlay ──
      // If this item is sitting on a surface (detected via surfaceMap), skip its
      // floor footprint — it doesn't physically occupy floor tiles in that case.
      const fpTilesArr = furniture.furniture_catalog?.footprint_tiles;
      const itemTileCount = fpTilesArr?.length ?? (depth.effectiveWidthTiles * depth.effectiveHeightTiles);
      const baseForSurfCheck = getOccupiedBaseTile(depth, isMirrored);
      const { isOnSurface: itemIsOnSurface } = surfaceMap.size > 0
        ? checkSurfaceOverlap(
            baseForSurfCheck.rawBaseTileX, baseForSurfCheck.rawBaseTileY,
            baseForSurfCheck.overlayWidthTiles, baseForSurfCheck.overlayHeightTiles,
            surfaceMap, fId, itemTileCount,
          )
        : { isOnSurface: false };

      if (itemIsOnSurface) {
        // Draw all footprint tiles (both on-surface and off-surface) so the user sees
        // the full footprint regardless of how much of it overlaps the surface.
        // Uses rawBaseTileX so highlights are correct for surfaces outside the floor grid.
        if (fpTilesArr && fpTilesArr.length > 0) {
          const centerTileX = Math.round(depth.tileX);
          const centerTileY = Math.round(depth.tileY);
          for (const [dx, dy] of fpTilesArr) {
            const [tdx, tdy] = isMirrored ? [dy, dx] : [dx, dy];
            drawTileDiamond(centerTileX + tdx, centerTileY + tdy, fillColor, 0.28);
          }
        } else {
          for (let tx = 0; tx < baseForSurfCheck.overlayWidthTiles; tx++) {
            for (let ty = 0; ty < baseForSurfCheck.overlayHeightTiles; ty++) {
              drawTileDiamond(baseForSurfCheck.rawBaseTileX + tx, baseForSurfCheck.rawBaseTileY + ty, fillColor, 0.28);
            }
          }
        }
      } else {
        if (fpTilesArr && fpTilesArr.length > 0) {
          // Admin-defined footprint: draw the exact tiles (relative to center tile)
          const centerTileX = Math.round(depth.tileX);
          const centerTileY = Math.round(depth.tileY);
          for (const [dx, dy] of fpTilesArr) {
            const [tdx, tdy] = isMirrored ? [dy, dx] : [dx, dy];
            drawTileDiamond(centerTileX + tdx, centerTileY + tdy, fillColor, 0.28);
          }
        } else {
          for (let tx = 0; tx < baseForSurfCheck.overlayWidthTiles; tx++) {
            for (let ty = 0; ty < baseForSurfCheck.overlayHeightTiles; ty++) {
              drawTileDiamond(baseForSurfCheck.baseTileX + tx, baseForSurfCheck.baseTileY + ty, fillColor, 0.28);
            }
          }
        }
      }
    }

    // Draw surface tiles with purple highlight (admin-defined)
    const surfaceTiles = furniture.furniture_catalog?.surface_tiles;
    if (surfaceTiles && surfaceTiles.length > 0) {
      const centerTileX = Math.round(depth.tileX);
      const centerTileY = Math.round(depth.tileY);
      for (const [dx, dy] of surfaceTiles) {
        const [tdx, tdy] = isMirrored ? [dy, dx] : [dx, dy];
        drawTileDiamond(centerTileX + tdx, centerTileY + tdy, 0x8a2be2, 0.35);
      }
    }
  }
}
